// The organisation plane (ADR-0021, ADR-0022).
//
// An organisation is an identity and a staff roster. A workspace is a billing,
// content and security boundary. This screen is the first, and it is separate
// from /people precisely because the two are separate: /people answers "what may
// this person DO in this workspace", and this page answers "who is in this
// company at all".
//
// THE SENTENCE THIS PAGE HAS TO KEEP SAYING. Being on the roster grants access
// to nothing. Somebody added here as Admin can staff clients and cannot publish
// a post; they reach a workspace only when somebody gives them a role in it, on
// /people. That is the two-plane rule from ADR-0021 §2, and it is the thing
// people get wrong — so it is stated on the roster card, in the invitation copy,
// and again on the invitee's own screen when they accept.
//
// Everyone has a personal organisation from signup (ADR-0022), so this page is
// never empty and never has to teach the concept before it can show anything.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../lib/WorkspaceContext";
import { Loading, Spinner } from "../components/Spinner";
import { Monogram } from "../components/Monogram";
import { logError } from "../lib/logger";
import {
  agencyStaff, removeOrganisationMember, type AgencyStaff,
} from "../lib/agency";
import {
  createOrganisation, createTeam, createWorkspace, inviteToOrganisation,
  moveWorkspaceToOrganisation, myOrganisations, organisationSeats,
  setOrganisationHandle, setOrganisationMemberRole,
  transferOrganisationOwnership,
  ORG_ROLE_BLURB, ORG_ROLE_LABEL,
  type OrgRole, type Organisation, type Seats,
} from "../lib/organisation";

const ASSIGNABLE: Exclude<OrgRole, "owner">[] = ["admin", "manager", "member"];

export function Organisation() {
  const { workspaces, refresh } = useWorkspace();

  const [orgs, setOrgs] = useState<Organisation[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [staff, setStaff] = useState<AgencyStaff[]>([]);
  const [seats, setSeats] = useState<Seats | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;
  const [loading, setLoading] = useState(true);
  // What a removal deliberately left standing. A warning, never a receipt.
  const [retained, setRetained] = useState<
    { client_name: string; roles: string[]; reason: string }[]>([]);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Exclude<OrgRole, "owner">>("member");
  const [handleDraft, setHandleDraft] = useState("");
  const [teamName, setTeamName] = useState("");
  const [newOrgName, setNewOrgName] = useState("");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");

  const org = useMemo(
    () => orgs?.find((o) => o.organisation_id === activeId) ?? orgs?.[0] ?? null,
    [orgs, activeId]);

  const load = useCallback(async () => {
    const mine = await myOrganisations();
    setOrgs(mine);
    const current = mine.find((o) => o.organisation_id === activeId) ?? mine[0] ?? null;
    if (!current) { setStaff([]); setSeats(null); return; }
    setActiveId(current.organisation_id);
    setStaff(await agencyStaff(current.organisation_id).catch(() => []));
    setSeats(await organisationSeats(current.organisation_id).catch(() => null));
  }, [activeId]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((e) => {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "load_organisation" });
      })
      .finally(() => setLoading(false));
  }, [load]);

  const run = useCallback(async (fn: () => Promise<unknown>, key = "page") => {
    setBusyKey(key);
    setError(null);
    setDone(null);
    try {
      await fn();
      await load();
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusyKey(null);
    }
  }, [load, refresh]);

  const mayAdmin = org ? org.org_role === "owner" || org.org_role === "admin" : false;
  const isOwner = org?.is_owner ?? false;
  const personal = org?.kind === "personal";
  const noSeats = !!seats && seats.used >= seats.allowed;

  if (loading && !orgs) return <div className="people-page"><Loading label="Loading your organisations…" /></div>;

  return (
    <div className="people-page">
      <header className="people-head">
        <div>
          <h1>Organisations</h1>
          <p className="muted">
            Who is in your company, and which workspaces belong to it. Everyone has a personal
            organisation from the day they sign up — it is where your own workspaces and connected
            accounts live.
          </p>
        </div>
        <span className="shield-pill">
          Being on a roster grants access to nothing on its own.
        </span>
      </header>

      {error && <div className="card err">{error}</div>}
      {done && <div className="card notice ok-note">{done}</div>}

      {retained.length > 0 && (
        <div className="card err">
          <strong>They still have access at {retained.length === 1
            ? "1 client" : `${retained.length} clients`}.</strong>
          <p className="muted card-sub">
            These were granted by the client directly, not through your organisation, so removing
            them here does not end them. Ask the client to remove them.
          </p>
          <ul className="can-list">
            {retained.map((r) => (
              <li className="cannot" key={r.client_name}><strong>{r.client_name}</strong>
                {r.roles?.length > 0 && ` — ${r.roles.join(", ")}`}</li>
            ))}
          </ul>
          <button className="sm" onClick={() => setRetained([])}>Dismiss</button>
        </div>
      )}

      {orgs && orgs.length > 1 && (
        <div className="segmented people-filter" role="group" aria-label="Your organisations">
          {orgs.map((o) => (
            <button key={o.organisation_id} type="button"
                    aria-pressed={o.organisation_id === org?.organisation_id}
                    className={o.organisation_id === org?.organisation_id ? "on" : ""}
                    onClick={() => setActiveId(o.organisation_id)}>
              {o.name} <span className="tab-count">{o.workspaces}</span>
            </button>
          ))}
        </div>
      )}

      {org && (
        <div className="people-grid">
          <div className="people-col">
            {/* The roster. */}
            <div className="card">
              <div className="card-head">
                <h2>{org.name}</h2>
                <span className={personal ? "chip" : "chip ok"}>
                  {personal ? "Personal" : org.kind}
                </span>
              </div>
              <p className="muted card-sub">
                {personal
                  ? "The organisation created with your account. It cannot be transferred or "
                    + "deleted — it is where your own things live — but it can take on people and "
                    + "clients like any other."
                  : "You are " + ORG_ROLE_LABEL[org.org_role].toLowerCase() + " here. "
                    + ORG_ROLE_BLURB[org.org_role]}
              </p>

              <p className="notice card-sub">
                <strong>A role here is not access to anything.</strong> Somebody on this roster
                reaches a workspace only when they are given a role in it, on{" "}
                <Link to="/people">People</Link>. An Admin here can staff clients and cannot publish
                a post.
              </p>

              {staff.length === 0 && <p className="muted">Nobody on the roster yet.</p>}

              {staff.map((p) => (
                <div className="person" key={p.user_id}>
                  <div className="person-main">
                    <Monogram label={p.email ?? p.user_id} />
                    <div className="person-body">
                      <div className="person-head">
                        <strong>{p.email ?? p.user_id}</strong>
                        {p.is_owner && <span className="chip ok">Owner</span>}
                        {!p.is_owner && (
                          <span className="chip">{ORG_ROLE_LABEL[p.org_role] ?? p.org_role}</span>
                        )}
                      </div>
                      <div className="person-roles">
                        <span className="muted">
                          {p.clients === 0
                            ? "Not on any client"
                            : p.clients === 1 ? "On 1 client" : `On ${p.clients} clients`}
                          {p.teams?.length > 0 && ` · ${p.teams.join(", ")}`}
                        </span>
                      </div>
                    </div>

                    {mayAdmin && !p.is_owner && (
                      <div className="row invite-actions">
                        <select
                          value={p.org_role}
                          disabled={busy}
                          aria-label={`Role for ${p.email ?? "this person"}`}
                          onChange={(e) => void run(
                            () => setOrganisationMemberRole({
                              organisationId: org.organisation_id,
                              userId: p.user_id,
                              orgRole: e.target.value as Exclude<OrgRole, "owner">,
                            }),
                            `role:${p.user_id}`)}
                        >
                          {ASSIGNABLE.map((r) => (
                            <option key={r} value={r}>{ORG_ROLE_LABEL[r]}</option>
                          ))}
                        </select>

                        {isOwner && (
                          <button className="sm" disabled={busy}
                            onClick={() => {
                              if (!confirm(
                                `Make ${p.email ?? "this person"} the owner of ${org.name}?\n\n`
                                + "You become an Admin. Only they can transfer it back.")) return;
                              void run(
                                () => transferOrganisationOwnership(org.organisation_id, p.user_id),
                                `transfer:${p.user_id}`);
                            }}>
                            Make owner
                          </button>
                        )}

                        <button
                          className={busyKey === `remove:${p.user_id}` ? "sm danger is-busy" : "sm danger"}
                          disabled={busy}
                          onClick={() => {
                            /* The one door out. It ends their access at every
                               client this organisation placed them with, which
                               is a much bigger action than it looks, so the
                               number is in the question. */
                            if (!confirm(
                              `Remove ${p.email ?? "this person"} from ${org.name}?\n\n`
                              + (p.clients > 0
                                  ? `Their access ends immediately at ${p.clients === 1
                                      ? "the 1 client" : `all ${p.clients} clients`} `
                                    + "you placed them with, and they leave every team here.\n\n"
                                  : "")
                              + "Anything a client granted them directly is not yours to revoke "
                              + "and will be listed.")) return;
                            void run(async () => {
                              const result = await removeOrganisationMember({
                                organisationId: org.organisation_id, userId: p.user_id,
                              });
                              setDone(
                                `${p.email ?? "That person"} left ${org.name}`
                                + (result.clients_affected > 0
                                    ? ` and lost access at ${result.clients_affected} client`
                                      + `${result.clients_affected === 1 ? "" : "s"}.`
                                    : "."));
                              setRetained(result.retained ?? []);
                            }, `remove:${p.user_id}`);
                          }}
                        >
                          {busyKey === `remove:${p.user_id}` && <Spinner />}
                          {busyKey === `remove:${p.user_id}` ? "Removing…" : "Remove"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Invite. */}
            {mayAdmin && (
              <div className="card">
                <div className="card-head">
                  <h2>Invite someone</h2>
                  {seats && (
                    <span className={noSeats ? "chip warn" : "chip"}>
                      {seats.used} of {seats.allowed} seats
                    </span>
                  )}
                </div>
                <p className="muted card-sub">
                  They can already have an account or not — either way the invitation is waiting for
                  them the next time they sign in with that address.
                </p>

                <div className="invite-form">
                  <div>
                    <label htmlFor="org-invite-email">Email address</label>
                    <input id="org-invite-email" type="email" value={inviteEmail}
                           placeholder="name@company.com"
                           onChange={(e) => setInviteEmail(e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="org-invite-role">Role in the organisation</label>
                    <select id="org-invite-role" value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value as Exclude<OrgRole, "owner">)}>
                      {ASSIGNABLE.map((r) => (
                        <option key={r} value={r}>{ORG_ROLE_LABEL[r]}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="muted card-sub">{ORG_ROLE_BLURB[inviteRole]}</p>

                {noSeats && (
                  <p className="notice card-sub">
                    <strong>No seats left.</strong> Seats come from connected accounts — one more
                    for every {seats?.ratio} accounts under management — or can be bought outright.
                  </p>
                )}

                <button
                  className={busyKey === "invite" ? "primary is-busy" : "primary"}
                  disabled={busy || !inviteEmail.trim() || noSeats}
                  onClick={() => void run(async () => {
                    await inviteToOrganisation({
                      organisationId: org.organisation_id,
                      email: inviteEmail.trim(),
                      orgRole: inviteRole,
                    });
                    setInviteEmail("");
                    setDone(`Invitation sent. It is waiting for them at Invitations when they sign in.`);
                  }, "invite")}
                >
                  {busyKey === "invite" && <Spinner />}
                  {busyKey === "invite" ? "Sending invitation…" : "Send invitation"}
                </button>
              </div>
            )}
          </div>

          <div className="people-col">
            {/* Seats. */}
            {seats && (
              <div className="card">
                <div className="card-head">
                  <h2>Seats</h2>
                  <span className={noSeats ? "chip warn" : "chip ok"}>
                    {seats.used} of {seats.allowed}
                  </span>
                </div>
                {/* Itemised on purpose: "you have 7 seats" invites an argument,
                    and "1 included, 4 earned, 2 bought" answers it first. */}
                <ul className="can-list">
                  <li className="can">{seats.base} included on this plan.</li>
                  <li className={seats.from_connections > 0 ? "can" : "cannot"}>
                    {seats.from_connections} earned by the {seats.connections} connected account
                    {seats.connections === 1 ? "" : "s"} under management — one per {seats.ratio}.
                  </li>
                  <li className={seats.purchased > 0 ? "can" : "cannot"}>
                    {seats.purchased} bought outright.
                  </li>
                </ul>
                <p className="muted" style={{ fontSize: ".8rem", marginBottom: 0 }}>
                  Open invitations count against this, so twenty invitations on a three-seat plan is
                  not a cap that holds until everybody clicks.
                </p>
              </div>
            )}

            {/* Workspaces. */}
            <div className="card">
              <div className="card-head">
                <h2>Workspaces</h2>
                <span className="muted">
                  {org.workspaces === 1 ? "1 workspace" : `${org.workspaces} workspaces`}
                </span>
              </div>
              <p className="muted card-sub">
                A workspace holds connected accounts, media and posts. Moving one between
                organisations takes all of that with it and ends every agency authorisation on it.
              </p>

              {workspaces.filter((w) => w.organisation_id === org.organisation_id).map((w) => (
                <div className="row" key={w.workspace_id}
                     style={{ justifyContent: "space-between", marginBottom: ".4rem" }}>
                  <span><strong>{w.name}</strong>{w.is_owner && <span className="chip ok">Owner</span>}</span>
                  {w.is_owner && orgs && orgs.length > 1 && (
                    <select
                      defaultValue=""
                      disabled={busy}
                      aria-label={`Move ${w.name} to another organisation`}
                      onChange={(e) => {
                        const target = e.target.value;
                        e.target.value = "";
                        if (!target) return;
                        const to = orgs.find((o) => o.organisation_id === target);
                        if (!confirm(
                          `Move ${w.name} to ${to?.name}?\n\n`
                          + "Its connected accounts, media and posts go with it. Every agency "
                          + "authorisation on this workspace is ended — an agency authorised by "
                          + "its current owner must not follow it to a new one.")) return;
                        void run(
                          async () => {
                            const r = await moveWorkspaceToOrganisation(w.workspace_id, target);
                            setDone(
                              `${w.name} moved to ${to?.name}.`
                              + (r.engagements_ended > 0
                                  ? ` ${r.engagements_ended} agency authorisation`
                                    + `${r.engagements_ended === 1 ? " was" : "s were"} ended.`
                                  : ""));
                          },
                          `move:${w.workspace_id}`);
                      }}
                    >
                      <option value="">Move to…</option>
                      {orgs.filter((o) => o.organisation_id !== org.organisation_id).map((o) => (
                        <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}

              {mayAdmin && (
                <div className="row" style={{ marginTop: ".7rem" }}>
                  <input type="text" value={newWorkspaceName} placeholder="New workspace name"
                         aria-label="New workspace name" style={{ maxWidth: "16rem" }}
                         onChange={(e) => setNewWorkspaceName(e.target.value)} />
                  <button className={busyKey === "new-ws" ? "sm is-busy" : "sm"}
                          disabled={busy || !newWorkspaceName.trim()}
                          onClick={() => void run(async () => {
                            await createWorkspace({
                              name: newWorkspaceName.trim(),
                              organisationId: org.organisation_id,
                            });
                            setNewWorkspaceName("");
                          }, "new-ws")}>
                    {busyKey === "new-ws" && <Spinner />}
                    {busyKey === "new-ws" ? "Creating…" : "Create workspace"}
                  </button>
                </div>
              )}
            </div>

            {/* Handle — only meaningful for an organisation that takes clients. */}
            {isOwner && (
              <div className="card">
                <div className="card-head">
                  <h2>Handle</h2>
                  {org.handle && <span className="chip ok">@{org.handle}</span>}
                </div>
                <p className="muted card-sub">
                  The one identifier a client types to authorise you — said once, by you, rather
                  than spelled three ways by ten clients. Once anybody has authorised you on the
                  strength of it, it stops being changeable.
                </p>
                <div className="row">
                  <input type="text" value={handleDraft} placeholder={org.handle ?? "bluepeak"}
                         aria-label="Organisation handle" style={{ maxWidth: "16rem" }}
                         onChange={(e) => setHandleDraft(e.target.value.toLowerCase())} />
                  <button className={busyKey === "handle" ? "sm is-busy" : "sm"}
                          disabled={busy || !(handleDraft.trim() || org.handle)}
                          onClick={() => void run(async () => {
                            await setOrganisationHandle(
                              org.organisation_id, handleDraft.trim() || org.handle || "");
                            setHandleDraft("");
                          }, "handle")}>
                    {busyKey === "handle" && <Spinner />}
                    {org.handle ? "Change handle" : "Claim handle"}
                  </button>
                </div>
              </div>
            )}

            {/* Teams. */}
            {mayAdmin && (
              <div className="card">
                <div className="card-head"><h2>Teams</h2></div>
                <p className="muted card-sub">
                  A pod: a named set of your own people. Putting a team on a client is one grant
                  instead of one per person — and every change of who is in it is written into each
                  of those clients' own audit trails, because an agency quietly adding a seventh
                  person to somebody's Facebook Page is exactly what they are entitled to hear about.
                </p>
                <div className="row">
                  <input type="text" value={teamName} placeholder="e.g. Nike pod"
                         aria-label="New team name" style={{ maxWidth: "16rem" }}
                         onChange={(e) => setTeamName(e.target.value)} />
                  <button className={busyKey === "team" ? "sm is-busy" : "sm"}
                          disabled={busy || !teamName.trim()}
                          onClick={() => void run(async () => {
                            await createTeam(org.organisation_id, teamName.trim());
                            setTeamName("");
                          }, "team")}>
                    {busyKey === "team" && <Spinner />}
                    {busyKey === "team" ? "Creating…" : "Create team"}
                  </button>
                </div>
              </div>
            )}

            {/* A second organisation. */}
            <div className="card">
              <div className="card-head"><h2>Another organisation</h2></div>
              <p className="muted card-sub">
                For a second company, a brand group, or work you intend to hand over later — a
                personal organisation cannot be transferred, and one you create can be.
              </p>
              <div className="row">
                <input type="text" value={newOrgName} placeholder="BluePeak Creative"
                       aria-label="New organisation name" style={{ maxWidth: "16rem" }}
                       onChange={(e) => setNewOrgName(e.target.value)} />
                <button className={busyKey === "new-org" ? "sm is-busy" : "sm"}
                        disabled={busy || !newOrgName.trim()}
                        onClick={() => void run(async () => {
                          const made = await createOrganisation({ name: newOrgName.trim() });
                          setNewOrgName("");
                          setActiveId(made.id);
                        }, "new-org")}>
                  {busyKey === "new-org" && <Spinner />}
                  {busyKey === "new-org" ? "Creating…" : "Create organisation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
